import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Film, Play, Trash2 } from 'lucide-react';
import { PageHeading } from '@/app/PageHeading';
import { Alert } from '@/components/arc/alert/alert';
import { Button } from '@/components/arc/button/button';
import { Checkbox } from '@/components/arc/checkbox/checkbox';
import { Dialog, DialogClose, DialogContent } from '@/components/arc/dialog/dialog';
import { DropdownMenu } from '@/components/arc/dropdown-menu/dropdown-menu';
import { EmptyState } from '@/components/arc/empty-state/empty-state';
import {
  FilterToolbar,
  type FilterChip,
  type FilterField,
} from '@/components/arc/filter-toolbar/filter-toolbar';
import { SearchField } from '@/components/arc/search-field/search-field';
import { Skeleton } from '@/components/arc/skeleton/skeleton';
import { SortableDataTable, type DataColumn } from '@/components/arc/sortable-data-table/sortable-data-table';
import { useToastStack } from '@/components/arc/toast-stack/toast-stack';
import { AnalysisStatusBadge } from '@/components/status/StatusBadge';
import { useBulkAnalyze, useDeleteVideo, useSources, useVideos } from '@/api/hooks';
import { useJobs, type JobRecord } from '@/api/ws';
import { errorMessage } from '@/api/client';
import type { AnalysisStatus, AnalysisTier, Platform, VideoOut } from '@/api/types';
import { ANALYSIS_STATUSES, PLATFORMS } from '@/api/types';
import { useDebounce } from '@/lib/hooks';
import { useTimezone } from '@/module/MomentProvider';
import { useHref } from '@/module/basePath';
import { fmtDate, fmtDuration, fmtMoney, fmtNumber, labelize, truncate } from '@/lib/format';
import { AnalyzeDialog, AnalyzeOptions, fpsValue } from './AnalyzeDialog';
import styles from './VideosPage.module.css';

type VideoRow = { [K in keyof VideoOut]: VideoOut[K] };

const platformLabel = (p: string) => (p === 'ctv' ? 'CTV' : labelize(p));
const isStatus = (v: string): v is AnalysisStatus => (ANALYSIS_STATUSES as string[]).includes(v);
const isPlatform = (v: string): v is Platform => (PLATFORMS as string[]).includes(v);

export function VideosPage() {
  const navigate = useNavigate();
  const href = useHref();
  const tz = useTimezone();
  const { toast } = useToastStack();
  const [sp, setSp] = useSearchParams();
  const [q, setQ] = useState('');
  const dq = useDebounce(q, 350);
  const [selected, setSelected] = useState<string[]>([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [analyzeTarget, setAnalyzeTarget] = useState<VideoOut | null>(null);
  const [deleting, setDeleting] = useState<VideoOut | null>(null);

  // Filters live in the URL so the Sources page can link here with `?source=<id>`.
  const statusParam = sp.get('status') ?? '';
  const platformParam = sp.get('platform') ?? '';
  const status = isStatus(statusParam) ? statusParam : undefined;
  const platform = isPlatform(platformParam) ? platformParam : undefined;
  const sourceId = sp.get('source') || undefined;
  const setParam = (key: string, value: string | undefined) =>
    setSp(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: true },
    );

  const params = useMemo(
    () => ({ q: dq.trim() || undefined, status, platform, source_id: sourceId, limit: 50 }),
    [dq, status, platform, sourceId],
  );
  const videos = useVideos(params);
  const sources = useSources();
  const bulk = useBulkAnalyze();
  const del = useDeleteVideo();
  const { jobs } = useJobs();

  const rows = useMemo<VideoRow[]>(() => videos.data?.pages.flatMap((p) => p.items) ?? [], [videos.data]);
  const total = videos.data?.pages[0]?.total ?? rows.length;
  const sourceList = useMemo(() => sources.data?.pages.flatMap((p) => p.items) ?? [], [sources.data]);
  const sourceName = (id: string) => {
    const s = sourceList.find((x) => x.id === id);
    return s ? s.title || s.external_id : truncate(id, 12);
  };
  const jobByRef = useMemo(() => {
    const map = new Map<string, JobRecord>();
    for (const j of jobs) if (j.ref_id && !map.has(j.ref_id)) map.set(j.ref_id, j);
    return map;
  }, [jobs]);

  const fields: FilterField[] = [
    {
      id: 'status',
      label: 'Status',
      options: ANALYSIS_STATUSES.map((s) => ({ value: s, label: labelize(s) })),
    },
    {
      id: 'platform',
      label: 'Platform',
      options: PLATFORMS.map((p) => ({ value: p, label: platformLabel(p) })),
    },
    {
      id: 'source',
      label: 'Source',
      options: sourceList.map((s) => ({ value: s.id, label: s.title || s.external_id })),
    },
  ];
  const filters: FilterChip[] = [];
  if (status) filters.push({ id: 'status', label: 'Status', value: labelize(status) });
  if (platform) filters.push({ id: 'platform', label: 'Platform', value: platformLabel(platform) });
  if (sourceId) filters.push({ id: 'source', label: 'Source', value: sourceName(sourceId) });

  const addFilter = (chip: FilterChip, field: FilterField) => {
    // The menu reports the option label; map it back to the value.
    const option = field.options.find(
      (o) => (typeof o === 'string' ? o : (o.label ?? o.value)) === chip.value,
    );
    const value = option === undefined ? chip.value : typeof option === 'string' ? option : option.value;
    setParam(field.id, value);
  };
  const clearFilters = () =>
    setSp(
      (prev) => {
        const next = new URLSearchParams(prev);
        ['status', 'platform', 'source'].forEach((k) => next.delete(k));
        return next;
      },
      { replace: true },
    );

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await del.mutateAsync(deleting.id);
      setSelected((prev) => prev.filter((id) => id !== deleting.id));
      setDeleting(null);
    } catch {
      /* shown inline in the dialog */
    }
  };

  const columns: DataColumn<VideoRow>[] = [
    {
      key: 'title',
      label: 'Video',
      render: (_v, v) => (
        <span className={styles.videoCell}>
          <span className={styles.thumb} aria-hidden="true">
            {v.thumbnail_url && <img src={v.thumbnail_url} alt="" loading="lazy" />}
          </span>
          <span className={styles.videoText}>
            <Link to={href(`/videos/${v.id}`)} className={styles.videoLink} title={v.title || v.external_id}>
              {v.title || v.external_id}
            </Link>
            <span className={styles.sub}>
              {v.channel_name ? `${v.channel_name} · ` : ''}
              {platformLabel(v.platform)}
            </span>
          </span>
        </span>
      ),
    },
    {
      key: 'duration_sec',
      label: 'Duration',
      width: 76,
      numeric: true,
      render: (v) => <span className="mono">{fmtDuration(v as number | null)}</span>,
    },
    {
      key: 'analysis_status',
      label: 'Status',
      width: 104,
      render: (_v, v) => {
        const job = jobByRef.get(v.id);
        const live =
          job && (job.status === 'running' || job.status === 'queued') && v.analysis_status !== 'done';
        return (
          <span className={styles.statusCell}>
            <AnalysisStatusBadge status={v.analysis_status} />
            {live && (
              <span className={`${styles.progress} mono`} role="status">
                {job.status === 'running' ? `${Math.round((job.progress ?? 0) * 100)}%` : 'Waiting'}
              </span>
            )}
          </span>
        );
      },
    },
    {
      key: 'model_used',
      label: 'Model',
      width: 116,
      render: (_v, v) => (
        <span className={`${styles.model} mono`}>
          {v.model_used ?? 'Not analyzed'}
          {v.fps_used ? ` · ${v.fps_used} fps` : ''}
        </span>
      ),
    },
    {
      key: 'moment_count',
      label: 'Moments',
      width: 80,
      numeric: true,
      render: (v) => fmtNumber(v as number),
    },
    {
      key: 'cost_usd',
      label: 'Cost',
      width: 92,
      numeric: true,
      render: (v) => <span className="mono">{v == null ? '0' : fmtMoney(v as number)}</span>,
    },
    {
      key: 'analyzed_at',
      label: 'Analyzed',
      width: 100,
      render: (v) => (v ? fmtDate(v as string, tz) : <span className="text-muted">Not yet</span>),
    },
    {
      key: 'actions',
      label: 'Actions',
      width: 190,
      sortable: false,
      render: (_v, v) => (
        <span className={styles.actions}>
          <Button
            size="sm"
            variant="secondary"
            disabled={v.analysis_status === 'analyzing' || v.analysis_status === 'queued'}
            onClick={() => setAnalyzeTarget(v)}
          >
            {v.analysis_status === 'done' ? 'Reanalyze' : 'Analyze'}
          </Button>
          <DropdownMenu
            label="More"
            items={[
              {
                label: 'Open moments',
                icon: <Play size={16} strokeWidth={1.75} />,
                onSelect: () => navigate(href(`/videos/${v.id}`)),
              },
              {
                label: 'Delete video',
                icon: <Trash2 size={16} strokeWidth={1.75} />,
                destructive: true,
                separatorBefore: true,
                onSelect: () => setDeleting(v),
              },
            ]}
          />
        </span>
      ),
    },
  ];

  const hasFilters = filters.length > 0 || dq.trim().length > 0;

  return (
    <main className="page">
      <PageHeading
        title="Videos"
        description={`${fmtNumber(total)} ${total === 1 ? 'video' : 'videos'} in the library. Open one to see its moment timeline.`}
        actions={
          selected.length > 0 ? (
            <Button onClick={() => setBulkOpen(true)}>
              <Play size={16} strokeWidth={1.75} aria-hidden="true" />
              Analyze {fmtNumber(selected.length)} selected
            </Button>
          ) : undefined
        }
        below={
          <div className={styles.toolbar}>
            <SearchField
              label="Search videos"
              placeholder="Title, channel or id"
              value={q}
              onValueChange={setQ}
              className={styles.search}
            />
            <FilterToolbar
              filters={filters}
              onRemove={(id) => setParam(id, undefined)}
              onClearAll={clearFilters}
              addFilter={{ fields, onAdd: addFilter }}
            />
          </div>
        }
      />

      {videos.isError ? (
        <Alert tone="danger" title="Could not load videos">
          <span className={styles.alertBody}>
            {errorMessage(videos.error)}
            <Button size="sm" variant="secondary" onClick={() => void videos.refetch()}>
              Try again
            </Button>
          </span>
        </Alert>
      ) : videos.isLoading ? (
        <Skeleton lines={6} label="Loading videos" />
      ) : rows.length === 0 ? (
        hasFilters ? (
          <EmptyState
            icon={<Film size={24} strokeWidth={1.5} />}
            title="No videos match"
            description="Try another search or remove a filter."
            action={
              <Button
                variant="secondary"
                onClick={() => {
                  setQ('');
                  clearFilters();
                }}
              >
                Clear search and filters
              </Button>
            }
          />
        ) : (
          <EmptyState
            icon={<Film size={24} strokeWidth={1.5} />}
            title="No videos yet"
            description="Add a YouTube channel, playlist or URL list under Sources and run discovery."
            action={<Button onClick={() => navigate(href('/sources'))}>Go to sources</Button>}
          />
        )
      ) : (
        <div
          className="stack-4"
          aria-busy={videos.isFetching && !videos.isFetchingNextPage ? true : undefined}
        >
          <div className="table-scroll">
            <SortableDataTable
              rows={rows}
              columns={columns}
              rowKey="id"
              caption="Videos"
              selectable
              selectedKeys={selected}
              onSelectionChange={setSelected}
              itemName={{ one: 'video', other: 'videos' }}
            />
          </div>
          {videos.hasNextPage && (
            <div className={styles.loadMore}>
              <Button
                variant="secondary"
                loading={videos.isFetchingNextPage}
                onClick={() => void videos.fetchNextPage()}
              >
                Load more videos
              </Button>
            </div>
          )}
        </div>
      )}

      <AnalyzeDialog video={analyzeTarget} onClose={() => setAnalyzeTarget(null)} />

      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent
          title={`Analyze ${fmtNumber(selected.length)} ${selected.length === 1 ? 'video' : 'videos'}`}
          description="Each video is queued as its own job. Progress shows in the jobs list."
        >
          {bulkOpen && (
            <BulkAnalyzeForm
              count={selected.length}
              pending={bulk.isPending}
              error={bulk.isError ? errorMessage(bulk.error) : null}
              onSubmit={async (tier, fps, onlyUnanalyzed) => {
                try {
                  const r = await bulk.mutateAsync({
                    video_ids: selected,
                    tier,
                    fps: fpsValue(fps),
                    only_unanalyzed: onlyUnanalyzed,
                  });
                  toast({
                    type: 'success',
                    title: `Queued ${fmtNumber(r.queued)} ${r.queued === 1 ? 'video' : 'videos'} for analysis`,
                  });
                  setSelected([]);
                  setBulkOpen(false);
                } catch {
                  /* shown inline in the dialog */
                }
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleting} onOpenChange={(open) => !open && setDeleting(null)}>
        <DialogContent
          title="Delete video?"
          description="Its moments are removed from every segment and export."
        >
          {deleting && (
            <p className={styles.dialogText}>
              <strong>{deleting.title || deleting.external_id}</strong>
            </p>
          )}
          {del.isError && (
            <Alert tone="danger" title="Could not delete the video">
              {errorMessage(del.error)}
            </Alert>
          )}
          <div className={styles.dialogActions}>
            <DialogClose asChild>
              <Button type="button" variant="secondary">
                Cancel
              </Button>
            </DialogClose>
            <Button variant="danger" loading={del.isPending} onClick={() => void confirmDelete()}>
              Delete video
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </main>
  );
}

function BulkAnalyzeForm({
  count,
  pending,
  error,
  onSubmit,
}: {
  count: number;
  pending: boolean;
  error: string | null;
  onSubmit: (tier: AnalysisTier, fps: string, onlyUnanalyzed: boolean) => void | Promise<void>;
}) {
  const [tier, setTier] = useState<AnalysisTier>('standard');
  const [fps, setFps] = useState('auto');
  const [onlyUnanalyzed, setOnlyUnanalyzed] = useState(true);
  return (
    <form
      className={styles.form}
      onSubmit={(e) => {
        e.preventDefault();
        void onSubmit(tier, fps, onlyUnanalyzed);
      }}
    >
      <AnalyzeOptions tier={tier} onTierChange={setTier} fps={fps} onFpsChange={setFps} />
      <Checkbox
        label="Skip videos that are already analyzed"
        description="Turn off to reanalyze finished videos with the options above."
        checked={onlyUnanalyzed}
        onCheckedChange={(v) => setOnlyUnanalyzed(v === true)}
      />
      {error && (
        <Alert tone="danger" title="Could not queue the analysis">
          {error}
        </Alert>
      )}
      <div className={styles.dialogActions}>
        <DialogClose asChild>
          <Button type="button" variant="secondary">
            Cancel
          </Button>
        </DialogClose>
        <Button type="submit" loading={pending}>
          Analyze {fmtNumber(count)} {count === 1 ? 'video' : 'videos'}
        </Button>
      </div>
    </form>
  );
}
